import { useCallback } from 'react'

import { useAuth } from '../../contexts/AuthContext'
import { apiUrl, useApiRequest } from '../useApiRequest'

import {
  Checkout,
  CheckoutQuote,
  CheckoutRequest,
  RescheduleRequest,
} from '../../types/booking/booking'

/**
 * Checkout (payment hold) endpoints. Creating with the same clientRequestId returns the same checkout, so retries,
 * double clicks and a second tab never create a second hold.
 */
export function useCheckoutApi() {
  const { saveAuthInfo } = useAuth()
  const { run, loading, error, resetState } = useApiRequest()

  const quote = useCallback(
    (request: CheckoutRequest) =>
      run(({ poster }) =>
        poster<CheckoutQuote>(apiUrl('/checkouts/quote'), request, 'application/json')
      ),
    [run]
  )

  const create = useCallback(
    (request: CheckoutRequest) =>
      run(({ poster }) =>
        poster<Checkout>(apiUrl('/checkouts'), request, 'application/json')
      ),
    [run]
  )

  const createGuest = useCallback(
    async (request: CheckoutRequest) => {
      const checkout = await run(({ poster }) =>
        poster<Checkout>(apiUrl('/guest/checkouts'), request, 'application/json')
      )
      if (checkout?.authInfo) {
        saveAuthInfo(checkout.authInfo)
      }
      return checkout
    },
    [run, saveAuthInfo]
  )

  const get = useCallback(
    (checkoutId: string) =>
      run(({ fetcher }) => fetcher<Checkout>(apiUrl(`/checkouts/${checkoutId}`))),
    [run]
  )

  const cancel = useCallback(
    (checkoutId: string) =>
      run(({ poster }) =>
        poster<Checkout>(apiUrl(`/checkouts/${checkoutId}/cancel`), undefined, 'application/json')
      ),
    [run]
  )

  const reschedule = useCallback(
    (bookingId: number, request: RescheduleRequest) =>
      run(({ poster }) =>
        poster<Checkout>(apiUrl(`/bookings/${bookingId}/reschedule`), request, 'application/json')
      ),
    [run]
  )

  return { quote, create, createGuest, get, cancel, reschedule, loading, error, resetState }
}
